import { Modal as ModalBootstrap } from 'react-bootstrap';
import classNames from 'classnames';

import { CloseIcon, CloseLargeIcon } from '../../icons';
import { getAnimationStyles, useModalCloseAnimation } from './utilities/helpers';

import styles from './_index.module.scss';

export interface ModalProps {
  buttons?: React.ReactNode[];
  children: React.ReactNode;
  className?: string;
  hasCloseIcon?: boolean;
  isActive: boolean;
  isClosing?: boolean;
  isScrollable?: boolean;
  onClose?: () => void;
  shouldSlide?: boolean;
  sidebar?: React.ReactNode;
  size?: 'fullscreen' | 'large' | 'x-large';
  style?: React.CSSProperties;
  title?: string;
}

const Modal = ({
  buttons,
  children,
  className,
  hasCloseIcon = true,
  isActive,
  isClosing = false,
  isScrollable,
  onClose,
  shouldSlide,
  sidebar,
  size,
  style = {},
  title,
  ...props
}: ModalProps) => {
  const isFullscreen = size === 'fullscreen';
  const { handleClose, isModalClosing, isModalOpening } = useModalCloseAnimation({
    isActive,
    onClose,
    shouldSlide,
  });

  const modalStyle = {
    ...style,
    ...getAnimationStyles({ shouldSlide }),
  };

  return (
    <ModalBootstrap
      animation={false}
      backdropClassName={styles['modal-backdrop']}
      centered
      className={classNames(
        styles['modal'],
        {
          [styles['is-large']]: size === 'large',
        },
        {
          [styles['is-x-large']]: size === 'x-large',
        },
        {
          [styles['is-fullscreen']]: isFullscreen,
        },
        {
          [styles['is-closing']]: shouldSlide && (isModalClosing || isClosing),
          [styles['is-opening']]: shouldSlide && isModalOpening,
        },
        className
      )}
      fullscreen={isFullscreen || undefined}
      scrollable={isScrollable}
      show={isActive}
      style={modalStyle}
      {...props}
    >
      {sidebar && <div className={styles['modal-sidebar']}>{sidebar}</div>}
      <div className={styles['modal-content-wrapper']}>
        {(title || (hasCloseIcon && onClose)) && (
          <div className={styles['modal-header']}>
            {title && <h2>{title}</h2>}
            {hasCloseIcon && onClose && (
              <button
                aria-label="Close"
                className={classNames(styles['modal-close'], {
                  [styles['modal-close-fullscreen']]: !title && isFullscreen,
                })}
                data-testid="modal-close"
                onClick={shouldSlide ? handleClose : onClose}
              >
                {isFullscreen ? <CloseLargeIcon /> : <CloseIcon />}
              </button>
            )}
          </div>
        )}
        <ModalBootstrap.Body>{children}</ModalBootstrap.Body>
        {buttons && buttons.length > 0 && (
          <div
            className={classNames(styles['modal-footer'], {
              [styles['footer-center']]: isFullscreen,
            })}
          >
            {buttons}
          </div>
        )}
      </div>
    </ModalBootstrap>
  );
};

export default Modal;
